/**
 * Pure decision logic for the in-session leaderboard toast pipeline.
 * Lives outside any React tree so the trigger matrix is fully unit-testable
 * and **shared between mobile and web** — one source of truth for "when
 * does the +N MP toast fire."
 *
 * ### Three toast kinds
 *   1. `milestone` — fires when the DOMINANT group's running display-MP
 *      sum crosses a defined MP tier (25, 50, 75, 100, 125, 150, 200, 250).
 *      Tiers between (e.g. 175, 225) and beyond 250 are intentionally
 *      silent — the named tiers map to curated variant pools.
 *   2. `combo` — fires when the in-session correct-in-a-row counter
 *      lands EXACTLY on 5, 10, 15, or 20. Past 20 it stays silent.
 *   3. `streakActive` — does NOT live in this module. The per-day
 *      streak-tier toast is driven by the liveScore.streak watcher in
 *      the lifecycle hook, since it depends on the server ack arriving
 *      after the first card (post-bump streak), not on a synchronous
 *      per-card decision.
 *
 * ### No caps, no cooldowns
 * Earlier iterations carried a 3-toast cap and an 8-card cooldown.
 * Both are intentionally removed — the new tier-based design self-limits
 * (one toast per tier crossing, at most ~8 milestones + 4 combos in a
 * single session) and any rate-limit feel should come from the spacing
 * of the events themselves.
 *
 * ### One trigger per card
 * Milestone takes priority over combo when both would fire on the same
 * card (combo will recover on the next +5 correct anyway, while a
 * skipped milestone tier never re-arms).
 */

/** Defined MP milestone tiers (display MP). A boundary crossing in this
 *  set fires a milestone toast; tiers outside this set are silent. */
export const MILESTONE_TIERS = [25, 50, 75, 100, 125, 150, 200, 250] as const;
export type MilestoneTier = (typeof MILESTONE_TIERS)[number];

/** Combo thresholds (correct in a row). Past 20 the combo silently
 *  continues counting up but no further combo toasts fire. */
export const COMBO_TIERS = [5, 10, 15, 20] as const;
export type ComboTier = (typeof COMBO_TIERS)[number];

/** Streak tiers that produce a "new multiplier activated" toast. Driven
 *  by the lifecycle hook (post-ack) — included here only as the source
 *  of truth shared with the UI layer. */
export const STREAK_TIERS = [2, 3, 5, 7, 14, 30] as const;
export type StreakTier = (typeof STREAK_TIERS)[number];

export type ToastTrigger =
  | {
      kind: 'milestone';
      /** MP tier crossed (one of MILESTONE_TIERS). Same value used in the
       *  variant pool lookup and the displayed `+N MP` text. */
      tier: MilestoneTier;
    }
  | {
      kind: 'combo';
      /** Combo count (one of COMBO_TIERS). */
      count: ComboTier;
    }
  | {
      kind: 'streakActive';
      /** Streak tier achieved today (one of STREAK_TIERS). */
      streak: StreakTier;
      /** Multiplier for that tier (e.g. 1.05 for Day 2, 1.5 for Day 7). */
      multiplier: number;
    };

export type ToastGuards = {
  /**
   * Highest milestone tier already toasted in this session (0 if none).
   * Monotonically increasing — once tier T fires, no tier ≤ T fires
   * again until a fresh session reset.
   */
  lastMilestoneShown: number;
  /** Current correct-in-a-row count. Resets to 0 on a `correct=false` card. */
  comboCount: number;
};

export type ToastDecisionEvent = {
  /**
   * `true` when this card SUSTAINS the combo:
   *   - SR: grade is `good` or `easy` (hard + bad both break the combo)
   *   - FR: grade is `good` (anything else breaks)
   *   - Quiz / exam: `success` or `medium` (wrong + skipped break)
   *
   * `false` resets `comboCount` to 0.
   */
  correct: boolean;
  /** Dominant group's running display-MP sum AFTER this card lands. The
   *  milestone trigger compares against `lastMilestoneShown` to spot the
   *  highest tier newly crossed. */
  dominantSessionMPAfter: number;
};

export type ToastDecision = {
  trigger: ToastTrigger | null;
  nextGuards: ToastGuards;
};

/**
 * Highest milestone tier in `MILESTONE_TIERS` that's `> lastShown` AND
 * `<= sum`. Returns null when no new tier is crossed.
 *
 * `MILESTONE_TIERS` is small and sorted ascending; we walk it once and
 * track the latest match. The "highest crossed" choice handles the rare
 * case of a single high-multiplier card jumping past multiple tiers in
 * one go — we surface the most impressive boundary instead of bursting
 * several toasts.
 */
function pickMilestoneTier(sum: number, lastShown: number): MilestoneTier | null {
  let picked: MilestoneTier | null = null;
  for (const tier of MILESTONE_TIERS) {
    if (tier > lastShown && tier <= sum) {
      picked = tier;
    }
  }
  return picked;
}

function isComboTier(count: number): count is ComboTier {
  return (COMBO_TIERS as readonly number[]).includes(count);
}

export function decideToast({
  guards,
  event,
}: {
  guards: ToastGuards;
  event: ToastDecisionEvent;
}): ToastDecision {
  const nextCombo = event.correct ? guards.comboCount + 1 : 0;

  const milestoneTier = pickMilestoneTier(
    event.dominantSessionMPAfter,
    guards.lastMilestoneShown,
  );

  let trigger: ToastTrigger | null = null;
  let nextMilestoneShown = guards.lastMilestoneShown;

  if (milestoneTier !== null) {
    trigger = { kind: 'milestone', tier: milestoneTier };
    nextMilestoneShown = milestoneTier;
  } else if (isComboTier(nextCombo)) {
    trigger = { kind: 'combo', count: nextCombo };
  }

  return {
    trigger,
    nextGuards: {
      lastMilestoneShown: nextMilestoneShown,
      comboCount: nextCombo,
    },
  };
}

/** Type-guard re-export — handy for lifecycle code gating on streak tier. */
export function isStreakTier(streak: number): streak is StreakTier {
  return (STREAK_TIERS as readonly number[]).includes(streak);
}
